/**
 * An application-owned deadline for a callable, because the SDK's is not one.
 *
 * THE DEFECT THIS EXISTS FOR, measured 2026-09-02. The Firebase Functions SDK
 * awaits the auth-token fetch BEFORE it starts its own timeout race. From the
 * bundle served to `dev.documentspark.com`, `@firebase/functions` 0.11.8:
 *
 *     const a = await t.contextProvider.getContext(...);   // awaited, NOT raced
 *     const l = r.timeout||7e4, c = lpe(l);                // the timer starts HERE
 *     const u = await Promise.race([hpe(...), c.promise, t.cancelAllRequests]);
 *
 * So the `timeout` option passed to `httpsCallable` covers the `fetch` and
 * nothing before it. `getContext()` -> `getAuthToken()` wraps its await in
 * try/catch, which converts a REJECTION into an unauthenticated call — fine —
 * but does nothing at all about a promise that never SETTLES.
 *
 * If it hangs: no POST is sent, no `deadline-exceeded` fires, the caller's
 * `catch` never runs, its `finally` never runs, the spinner spins forever and
 * the control stays disabled. There is no message, and nothing is recorded
 * anywhere — the app's global `unhandledrejection` capture sees nothing,
 * because a hang raises no event. The only exit is a page reload.
 *
 * This wrapper makes that state unreachable: past the deadline the call becomes
 * a normal rejection, which every existing `catch` already knows how to show.
 *
 * IT DOES NOT CANCEL ANYTHING. There is no cancellation in the callable
 * protocol; the server keeps working and may well finish. That is why the
 * message says so rather than claiming the operation failed — telling a user
 * their run failed when it is about to succeed is its own defect.
 *
 * Deliberately transport-agnostic: it takes a thunk, not a callable, so it can
 * wrap any of the 171 `httpsCallable` sites in `web/src`, none of which share a
 * wrapper today. Applied so far to the Verification re-run only.
 */

/** Rejection raised when a call outlives its deadline. */
export class DeadlineExceededError extends Error {
  readonly deadlineMs: number;
  readonly label?: string;

  constructor(deadlineMs: number, label?: string) {
    const seconds = Math.round(deadlineMs / 1000);
    const subject = label ? `${label} did not` : 'The request did not';
    super(
      `${subject} respond within ${seconds} seconds (deadline ${deadlineMs} ms). ` +
        'It may still be running on the server — the results refresh on their own ' +
        'when it finishes. Reload the page to see the current state.',
    );
    this.name = 'DeadlineExceededError';
    this.deadlineMs = deadlineMs;
    this.label = label;
  }
}

/**
 * Run `invoke()` and reject if it has not settled within `deadlineMs`.
 *
 * `label` names the operation in the message a user will read — "The
 * verification run", not "runValidationV2".
 *
 * The timer is cleared however the call settles, so a resolved call leaves
 * nothing armed to fire into an unmounted component. A late rejection from the
 * losing side of the race is already handled by `Promise.race`, which attaches
 * its own handler to every input, so it cannot surface as an unhandled
 * rejection and reach the global error reporter as noise.
 */
export function callWithDeadline<T>(
  invoke: () => Promise<T>,
  deadlineMs: number,
  label?: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new DeadlineExceededError(deadlineMs, label)), deadlineMs);
  });

  // `Promise.resolve().then(invoke)` so a thunk that throws synchronously
  // becomes a rejection rather than escaping past the race entirely.
  const call = Promise.resolve().then(invoke);

  return Promise.race([call, deadline]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  }) as Promise<T>;
}
